import React, { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, FileText, Trash2, UploadCloud } from "lucide-react";
import { AuthContext } from "../../../context/authContext";
import {
  uploadShipmentDoc,
} from "../../../reducers/shipment.slice";
import {
  uploadBarcodeRedemptionApi,
} from "../../../reducers/payouts.slice";
import { encodeHashData } from "../../../utils/hashing";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const GiftCardRedemptionPage = ({ dialogMode = false, requestDetail: requestDetailProp, type: typeProp, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { requestDetail: routeRequestDetail, type: routeType } = location?.state || {};
  const requestDetail = requestDetailProp || routeRequestDetail;
  const type = typeProp || routeType;
  const [showLoader, setShowLoader] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState({});

  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileUpload(files);
  };

  const handleFileUpload = (files) => {
    if (uploadedFiles.length >= 1) {
      showToast("Only one image is allowed. Please remove the existing one first.", "error");
      return;
    }
    if (files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        showToast("Invalid file type. Only JPG and PNG files are allowed.", "error");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast("File is too large. Maximum size allowed is 5MB.", "error");
        return;
      }
      setUploadedFiles([file]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateWithdrawalRequest = async () => {
    setShowLoader(true);
    const payloadFormData = new FormData();
    uploadedFiles.forEach((file) => {
      payloadFormData.append("files", file);
    });
    const encodedGiftCardCode = encodeHashData(formData.gift_card_code || "");
    const encodedGiftCardPin = encodeHashData(formData.gift_card_pin || "");

    payloadFormData.append("gift_card_code", encodedGiftCardCode);
    payloadFormData.append("gift_card_pin", encodedGiftCardPin);
    payloadFormData.append("id", requestDetail?.id);
    payloadFormData.append("status", "READY_FOR_REDEMPTION");

    await dispatch(uploadBarcodeRedemptionApi(payloadFormData)).then((resp) => {
      if (resp?.error && Object.keys(resp?.error)?.length > 0) {
        setShowLoader(false);
        showToast(resp?.error?.message, "error");
        return;
      }
      showToast("Request updated successfully", "success");
      setShowLoader(false);
      if (dialogMode && onSuccess) onSuccess();
      else navigate(-1);
    });
  };

  const alphanumericOnly = (value) => value.replace(/[^a-zA-Z0-9]/g, "");
  const alphanumericKey = (e) => {
    if (
      !/^[a-zA-Z0-9]$/.test(e.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  return (
    <>
      {showLoader && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Loading...
        </div>
      )}

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          padding: dialogMode ? 0 : isMobile ? "8px 0 32px" : "24px 0 48px",
        }}
      >
        <div style={{ width: "100%", maxWidth: 560 }}>
          {!dialogMode && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#c3c8cc",
                background: "transparent",
                border: "none",
                padding: 0,
                marginBottom: 18,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              <ArrowLeft size={16} /> Back
            </button>
          )}

          <h2 style={{ fontSize: "2rem", fontWeight: 700, color: "#fff", margin: "0 0 16px" }}>
            Approve {type === "withdraw" ? "Payment" : "Gift Card Request"}
          </h2>

          {/* Gift Card Code */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
            <Label htmlFor="gift_card_code">Gift Card Code</Label>
            <Input
              id="gift_card_code"
              name="gift_card_code"
              type="text"
              value={formData.gift_card_code || ""}
              placeholder="Enter Gift Card Code"
              onChange={(e) =>
                setFormData({ ...formData, gift_card_code: alphanumericOnly(e.target.value) })
              }
              onKeyDown={alphanumericKey}
            />
          </div>

          {/* Gift Card Pin */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
            <Label htmlFor="gift_card_pin">Gift Card Pin</Label>
            <Input
              id="gift_card_pin"
              name="gift_card_pin"
              type="text"
              value={formData.gift_card_pin || ""}
              placeholder="Enter Gift Card Pin"
              onChange={(e) =>
                setFormData({ ...formData, gift_card_pin: alphanumericOnly(e.target.value) })
              }
              onKeyDown={alphanumericKey}
            />
          </div>

          {/* Upload area */}
          <button
            type="button"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-input").click()}
            style={{
              width: "100%",
              minHeight: 200,
              border: `1px dashed ${dragOver ? "#00ADB5" : "rgba(255,255,255,0.28)"}`,
              borderRadius: 8,
              marginTop: 8,
              marginBottom: 16,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              background: dragOver ? "rgba(0,173,181,0.08)" : "rgba(255,255,255,0.025)",
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <UploadCloud size={42} color="#00ADB5" />
              <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>
                Click here or Drop a file to upload Barcode
              </span>
              <span style={{ color: "#8b9296", fontSize: 12 }}>
                (Only one file allowed: .jpg, .png)
              </span>
            </span>
            <input
              id="file-input"
              type="file"
              accept=".jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />
          </button>

          {/* Uploaded files list */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, marginBottom: 10 }}>
              Uploaded Files
            </div>
            {uploadedFiles.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.025)",
                      padding: 12,
                      borderRadius: 8,
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minWidth: 0,
                        color: "#c3c8cc",
                        fontSize: 13,
                      }}
                    >
                      <FileText size={15} style={{ flexShrink: 0 }} />
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                      style={{ color: "#f27059", flexShrink: 0 }}
                    >
                      <Trash2 size={14} /> Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#8b9296", fontSize: 13 }}>No files uploaded</div>
            )}
          </div>

          <Button
            type="button"
            onClick={handleUpdateWithdrawalRequest}
            disabled={!formData.gift_card_code || !formData.gift_card_pin}
            style={{ width: "100%" }}
          >
            Submit
          </Button>
          {dialogMode && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              style={{ width: "100%", marginTop: 10 }}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default GiftCardRedemptionPage;
